/**
 * FeedbackHistoryPanel (iter156, extended iter157) — Coach-side review of every
 * post-session feedback entry ever logged for a specific player.
 *
 * Privacy: backend restricts the list to entries the current coach wrote
 * (admins + the player themselves see all). This component just renders
 * what the API returns.
 *
 * Per-entry actions:
 *   · Copy — plain-text of topics + notes + snapshot to clipboard.
 *   · Reuse — opens a multi-select picker of pending sessions:
 *       - Ticking a single row → opens the feedback modal prefilled.
 *       - Ticking N rows + "Apply to all" → calls the bulk endpoint.
 *
 * Iter157 additions:
 *   · Renders "Reused from [player] · [date]" chip when reused_from is set.
 *   · Multi-select in the reuse picker with a "Selected N" pill and
 *     "Apply to selected" primary CTA.
 *   · Bulk apply → POST /coach/post-session-feedback/bulk.
 */
import { useEffect, useMemo, useState } from "react";
import { Copy, RotateCw, Loader2, Sparkles, X, Users, Check, Send, Heart, Pencil, Flag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSport } from "@/context/SportContext";
import PostSessionFeedbackModal from "@/components/PostSessionFeedbackModal";

const humanise = (slug) =>
  String(slug || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso).slice(0, 16); }
};
const fmtShortDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return String(iso).slice(0, 10); }
};

function entryToPlainText(fb) {
  const lines = [];
  if (fb.session_title) lines.push(`Session: ${fb.session_title}`);
  if (fb.session_start) lines.push(`When: ${fmtDate(fb.session_start)}`);
  const topics = fb.confirmed_topics || [];
  if (topics.length) {
    lines.push("", "Topics covered:");
    topics.forEach((t) => lines.push(`  · ${humanise(t)}`));
  }
  const perSkill = fb.per_skill_notes || {};
  if (Object.keys(perSkill).length) {
    lines.push("", "Per-topic notes:");
    for (const [k, v] of Object.entries(perSkill)) {
      lines.push(`  · ${humanise(k)} — ${v}`);
    }
  }
  if (fb.general_notes) {
    lines.push("", "General notes:", fb.general_notes);
  }
  const snap = fb.skill_snapshot || {};
  if (Object.keys(snap).length) {
    lines.push("", "Skill snapshot (1–5):");
    for (const [k, v] of Object.entries(snap)) {
      lines.push(`  · ${humanise(k)}: ${v}`);
    }
  }
  if (fb.ai_summary) {
    lines.push("", "Recap:", fb.ai_summary);
  }
  return lines.join("\n");
}

export default function FeedbackHistoryPanel({ studentId, studentName = "" }) {
  const { skillsBySport, sport } = useSport();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copyingId, setCopyingId] = useState("");
  const [reuseSource, setReuseSource] = useState(null); // entry being reused
  const [pending, setPending] = useState([]);           // pending-feedback list
  const [pickerSelection, setPickerSelection] = useState({}); // {bookingId-studentId: true}
  const [reuseTarget, setReuseTarget] = useState(null); // {booking_id, student_id, ...} for single-modal path
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = async (silent = false) => {
    if (!studentId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/post-session-feedback`);
      setEntries(data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        setEntries([]);
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't load feedback history");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyEntry = async (fb) => {
    setCopyingId(fb.id);
    try {
      const text = entryToPlainText(fb);
      await navigator.clipboard.writeText(text);
      toast.success("Notes copied — paste anywhere");
    } catch {
      toast.error("Clipboard unavailable — long-press to copy manually");
    } finally { setCopyingId(""); }
  };

  const openReusePicker = async (fb) => {
    setReuseSource(fb);
    setPickerSelection({});
    try {
      const { data } = await api.get("/me/coach/pending-feedback");
      const rows = (data?.items || []).filter((it) => it.student_id !== studentId);
      setPending(rows);
      if (rows.length === 0) {
        toast.info("No other pending sessions to reuse into right now");
      }
    } catch {
      setPending([]);
      toast.error("Couldn't load your pending sessions");
    }
  };

  const closeReusePicker = () => {
    setReuseSource(null);
    setPending([]);
    setPickerSelection({});
  };

  const rowKey = (row) => `${row.booking_id}-${row.student_id}`;
  const toggleSelect = (row) => {
    const k = rowKey(row);
    setPickerSelection((prev) => ({ ...prev, [k]: !prev[k] }));
  };
  const selectedRows = useMemo(
    () => pending.filter((r) => pickerSelection[rowKey(r)]),
    [pending, pickerSelection]
  );

  // Iter157 — build the reused_from metadata we forward to backend so every
  // resulting doc carries a "Reused from [player] · [date]" chip.
  const provenance = () => reuseSource ? ({
    feedback_id: reuseSource.id,
    student_id: studentId,
    student_name: studentName,
    session_title: reuseSource.session_title || "",
    source_created_at: reuseSource.created_at,
  }) : null;

  const startSingle = (row) => {
    // Selecting a single target from the picker just opens the modal.
    setPickerSelection({ [rowKey(row)]: true });
    setReuseTarget(row);
  };

  const applyBulk = async () => {
    if (!selectedRows.length || !reuseSource) return;
    setBulkSubmitting(true);
    try {
      const template = {
        confirmed_topics: reuseSource.confirmed_topics || [],
        per_skill_notes: reuseSource.per_skill_notes || {},
        general_notes: reuseSource.general_notes || "",
        skill_snapshot: reuseSource.skill_snapshot || {},
        reused_from: provenance(),
      };
      const targets = selectedRows.map((r) => ({ student_id: r.student_id, session_id: r.booking_id }));
      const { data } = await api.post(`/coach/post-session-feedback/bulk`, { targets, template });
      const ok = data?.successes || 0;
      const bad = data?.failures || 0;
      if (ok && !bad) toast.success(`Applied to ${ok} session${ok === 1 ? "" : "s"}`);
      else if (ok && bad) toast.warning(`Applied to ${ok}, ${bad} failed — see console`);
      else toast.error(`No sessions could be updated`);
      if (bad && data?.results) {
        // Log per-row failures so the coach can chase them.
        console.warn("[bulk feedback failures]", data.results.filter((r) => !r.ok));
      }
      closeReusePicker();
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Bulk apply failed");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const finishSingleReuse = () => {
    setReuseTarget(null);
    closeReusePicker();
    load();
  };

  const currentSkills = useMemo(
    () => (reuseTarget ? (skillsBySport?.[reuseTarget.sport || sport] || []) : []),
    [reuseTarget, skillsBySport, sport]
  );

  if (loading) {
    return (
      <div className="card-surface p-6 flex items-center gap-2 text-sm text-[#A3A3A3]" data-testid="feedback-history-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading feedback history…
      </div>
    );
  }

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="card-surface p-6 fade-up" data-testid="feedback-history-panel">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-[#CCFF00]" />
        <div className="label-mono">POST-SESSION FEEDBACK LOG · {entries.length}</div>
      </div>
      <p className="text-xs text-[#A3A3A3] mb-4">
        Every recap you've logged for {studentName || "this player"}. Copy to paste anywhere, or reuse for other players (bulk supported).
      </p>
      <div className="space-y-3">
        {entries.map((fb) => (
          <div
            key={fb.id}
            data-testid={`feedback-history-entry-${fb.id}`}
            className="bg-[#0A0A0A] border border-[#262626] p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-semibold">{fb.session_title || "Session"}</div>
                <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                  {fmtDate(fb.session_start)} · logged {fmtDate(fb.created_at)}
                  {fb.regen_count > 0 && (
                    <span className="ml-1.5 text-[#66C2FF]" data-testid={`regen-indicator-${fb.id}`}>
                      · regenerated ×{fb.regen_count}
                    </span>
                  )}
                </div>
                {fb.reused_from && (
                  <div
                    className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF]"
                    data-testid={`reused-from-chip-${fb.id}`}
                    title={`Copied from ${fb.reused_from.student_name || "another player"}'s notes on ${fmtDate(fb.reused_from.source_created_at)}`}
                  >
                    <RotateCw className="w-3 h-3" />
                    Reused from {fb.reused_from.student_name || "another player"}
                    {fb.reused_from.source_created_at && ` · ${fmtShortDate(fb.reused_from.source_created_at)}`}
                  </div>
                )}
                {/* Iter177 — Player reaction badge. Only rendered when the
                    student has tapped a reaction; keeps the row quiet otherwise. */}
                {fb.reaction && (fb.reaction.emoji || fb.reaction.note) && (
                  <div
                    className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] ml-1.5"
                    data-testid={`reaction-badge-${fb.id}`}
                    title={`${(studentName || "Player")} reacted at ${fmtDate(fb.reaction.reacted_at)}`}
                  >
                    <Heart className="w-3 h-3" strokeWidth={2.5} />
                    {fb.reaction.emoji || ""} {fb.reaction.note ? `“${fb.reaction.note}”` : "Reacted"}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => copyEntry(fb)}
                  disabled={copyingId === fb.id}
                  data-testid={`copy-feedback-${fb.id}`}
                  className="px-3 py-1.5 text-[11px] uppercase tracking-wider border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5 disabled:opacity-40"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <button
                  type="button"
                  onClick={() => openReusePicker(fb)}
                  data-testid={`reuse-feedback-${fb.id}`}
                  className="px-3 py-1.5 text-[11px] uppercase tracking-wider border border-[#404040] hover:border-[#66C2FF] hover:text-[#66C2FF] inline-flex items-center gap-1.5"
                >
                  <RotateCw className="w-3 h-3" /> Reuse
                </button>
                {(fb.skill_entries || []).length > 0 && !fb.team_feedback_batch_id && (
                  <button
                    type="button"
                    onClick={() => setEditTarget(fb)}
                    data-testid={`edit-feedback-${fb.id}`}
                    className="px-3 py-1.5 text-[11px] uppercase tracking-wider border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                )}
              </div>
            </div>

            {(fb.confirmed_topics || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {fb.confirmed_topics.map((t) => (
                  <span key={t} className="px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#262626] text-[#CCFF00]">
                    {humanise(t)}
                  </span>
                ))}
              </div>
            )}

            {(fb.skill_entries || []).some((e) => e.covered) && (
              <div className="space-y-1" data-testid={`feedback-history-skills-${fb.id}`}>
                {fb.level_label && <div className="label-mono text-[#A3A3A3]">Rated at {fb.level_label} · coach view</div>}
                {fb.skill_entries.filter((e) => e.covered).map((e) => (
                  <div key={e.key} className="text-xs text-[#E5E5E5] flex gap-2">
                    <span className="font-mono text-[#CCFF00] w-10 shrink-0">{e.rating}/5</span>
                    <span className="text-[#A3A3A3]">{e.label || e.name}{e.carry_forward && <Flag className="inline w-3 h-3 ml-1 text-[#FF9F1C]" />}</span>
                    {e.coach_note && <span>— {e.coach_note}</span>}
                  </div>
                ))}
              </div>
            )}

            {Object.keys(fb.per_skill_notes || {}).length > 0 && (
              <div className="space-y-1.5">
                {Object.entries(fb.per_skill_notes).map(([slug, note]) => (
                  <div key={slug} className="text-xs text-[#E5E5E5]">
                    <span className="text-[#A3A3A3]">{humanise(slug)} — </span>
                    <span>{note}</span>
                  </div>
                ))}
              </div>
            )}

            {fb.general_notes && (
              <div className="text-xs text-[#E5E5E5] border-t border-[#262626] pt-2">
                <div className="label-mono text-[#A3A3A3] mb-1">General notes</div>
                {fb.general_notes}
              </div>
            )}

            {fb.ai_summary && (
              <div className="text-xs text-[#E5E5E5] border-l-2 border-[#CCFF00] pl-3 italic" data-testid={`feedback-history-summary-${fb.id}`}>
                {fb.ai_summary}
              </div>
            )}
          </div>
        ))}
      </div>

      {reuseSource && !reuseTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          data-testid="reuse-picker-modal"
          role="dialog"
        >
          <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto bg-[#0A0A0A] border border-[#262626] p-6 relative">
            <button
              type="button"
              onClick={closeReusePicker}
              data-testid="reuse-picker-close"
              className="absolute top-3 right-3 text-[#A3A3A3] hover:text-white"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="label-mono text-[#66C2FF] mb-1">REUSE FOR OTHER PLAYERS</div>
            <h2 className="h-display text-2xl mb-1">Tick one or many</h2>
            <p className="text-xs text-[#A3A3A3] mb-4">
              We'll prefill the feedback form with the topics, notes and snapshot from this entry. Tick a single row to edit before submit — tick several and hit <strong className="text-white">Apply to selected</strong> to batch it.
            </p>
            {pending.length === 0 ? (
              <div className="bg-[#141414] border border-[#262626] p-4 text-sm text-[#A3A3A3] text-center" data-testid="reuse-picker-empty">
                No other pending sessions to reuse into right now.
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {pending.map((row) => {
                    const k = rowKey(row);
                    const on = !!pickerSelection[k];
                    return (
                      <label
                        key={k}
                        data-testid={`reuse-row-${row.booking_id}-${row.student_id}`}
                        className="flex items-center gap-3 bg-[#141414] border border-[#262626] p-3 cursor-pointer hover:border-[#66C2FF]"
                        style={{ borderColor: on ? "#66C2FF" : undefined }}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleSelect(row)}
                          data-testid={`reuse-check-${row.booking_id}-${row.student_id}`}
                          className="w-4 h-4 accent-[#66C2FF]"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <Users className="w-3 h-3 text-[#66C2FF]" />
                            <div className="text-sm font-semibold">{row.student_name}</div>
                          </div>
                          <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                            {row.title} · {fmtDate(row.session_start)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); startSingle(row); }}
                          data-testid={`reuse-edit-${row.booking_id}-${row.student_id}`}
                          className="px-2 py-1 text-[10px] uppercase tracking-wider border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00]"
                        >
                          Edit →
                        </button>
                      </label>
                    );
                  })}
                </div>

                <div className="mt-5 flex items-center justify-between gap-3 pt-3 border-t border-[#262626]">
                  <div className="text-xs text-[#A3A3A3]" data-testid="reuse-selected-count">
                    {selectedRows.length === 0
                      ? "Nothing selected"
                      : `${selectedRows.length} selected`}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={closeReusePicker}
                      className="px-3 py-2 text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={applyBulk}
                      disabled={bulkSubmitting || selectedRows.length === 0}
                      data-testid="reuse-apply-bulk"
                      className="px-4 py-2 text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: "#66C2FF", color: "#0A0A0A" }}
                    >
                      {bulkSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      {bulkSubmitting
                        ? "Applying…"
                        : selectedRows.length > 1
                          ? `Apply to ${selectedRows.length} sessions`
                          : "Apply to selected"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editTarget && (
        <PostSessionFeedbackModal
          open
          onClose={() => setEditTarget(null)}
          onSaved={() => load(true)}
          bookingId={editTarget.session_id}
          studentId={studentId}
          studentName={studentName}
          sessionTitle={editTarget.session_title}
          editFeedback={editTarget}
        />
      )}

      {reuseTarget && reuseSource && (
        <PostSessionFeedbackModal
          open
          onClose={() => setReuseTarget(null)}
          onSaved={finishSingleReuse}
          bookingId={reuseTarget.booking_id}
          studentId={reuseTarget.student_id}
          studentName={reuseTarget.student_name}
          sessionTitle={reuseTarget.title}
          sport={reuseSource.sport || sport}
          prefill={{
            skill_entries: reuseSource.skill_entries || [],
            confirmed_topics: reuseSource.confirmed_topics || [],
            per_skill_notes: reuseSource.per_skill_notes || {},
            general_notes: reuseSource.general_notes || "",
            skill_snapshot: reuseSource.skill_snapshot || {},
            reused_from: provenance(),
          }}
        />
      )}
    </div>
  );
}
